import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2Icon,
  Loader2Icon,
  QrCodeIcon,
  RefreshCwIcon,
  SmartphoneIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { toast } from "sonner";

import { CopyButton } from "@/components/copy-button";
import { QrCode } from "@/components/qr-code";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PairMethod } from "@/gen/wabms/admin/v1/admin_pb";
import { invalidateAccounts } from "@/lib/admin-queries";
import { errorMessage, isCanceled } from "@/lib/errors";
import { adminClient } from "@/lib/transport";
import { cn } from "@/lib/utils";

/**
 * whatsmeow rotates the QR ref on roughly this cadence. The server is the
 * authority — a new `qr_code` update replaces whatever is on screen — so this
 * constant only scales the countdown bar between updates.
 */
const QR_TTL_SECONDS = 20;

interface AddAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type PairPhase = "idle" | "streaming" | "paired" | "timeout" | "error";

/**
 * Drives the PairAccount server stream.
 *
 * PairAccount is the only streaming RPC in the admin surface, so it does not go
 * through connect-query: the promise client yields an AsyncIterable of
 * PairAccountUpdate and this component renders one branch of the `update`
 * oneof at a time. The stream stays open for the whole pairing window, so the
 * transport's 30 s default timeout is disabled for this call and the request is
 * aborted whenever the dialog closes, the tab changes, or the component
 * unmounts — otherwise a closed dialog would leave a live socket and a server
 * goroutine waiting for a scan nobody is looking at.
 */
export function AddAccountDialog({ open, onOpenChange }: AddAccountDialogProps) {
  const queryClient = useQueryClient();

  const [method, setMethod] = useState<PairMethod>(PairMethod.QR);
  const [label, setLabel] = useState("");
  const [phone, setPhone] = useState("");
  const [phase, setPhase] = useState<PairPhase>("idle");
  const [qrPayload, setQrPayload] = useState<string | undefined>(undefined);
  const [rotation, setRotation] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(QR_TTL_SECONDS);
  const [linkingCode, setLinkingCode] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pairedLabel, setPairedLabel] = useState<string | undefined>(undefined);

  const abortRef = useRef<AbortController | undefined>(undefined);

  /** Cancels an in-flight PairAccount call, if any. Safe to call repeatedly. */
  const abortStream = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = undefined;
  }, []);

  const reset = useCallback(() => {
    abortStream();
    setPhase("idle");
    setQrPayload(undefined);
    setRotation(0);
    setSecondsLeft(QR_TTL_SECONDS);
    setLinkingCode(undefined);
    setError(undefined);
    setPairedLabel(undefined);
  }, [abortStream]);

  // Closing the dialog must tear the stream down, not just hide it.
  useEffect(() => {
    if (open) return;
    reset();
    setLabel("");
    setPhone("");
    setMethod(PairMethod.QR);
  }, [open, reset]);

  // Unmount is the last line of defence against a leaked stream.
  useEffect(() => abortStream, [abortStream]);

  // Purely cosmetic countdown between server-pushed QR codes. It never drives
  // a refresh of its own; when it hits zero it simply parks at zero until the
  // next `qr_code` update arrives.
  useEffect(() => {
    if (phase !== "streaming" || method !== PairMethod.QR || qrPayload === undefined) return;
    const handle = window.setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(handle);
  }, [phase, method, qrPayload, rotation]);

  const start = useCallback(async () => {
    const trimmedLabel = label.trim();
    if (trimmedLabel.length === 0) {
      setError("Give the account a label first.");
      setPhase("error");
      return;
    }
    const trimmedPhone = phone.trim();
    if (method === PairMethod.PHONE_CODE && !/^\d{8,15}$/.test(trimmedPhone)) {
      setError("Enter the number in E.164 digits, without a leading +.");
      setPhase("error");
      return;
    }

    abortStream();
    const controller = new AbortController();
    abortRef.current = controller;

    setError(undefined);
    setQrPayload(undefined);
    setLinkingCode(undefined);
    setRotation(0);
    setSecondsLeft(QR_TTL_SECONDS);
    setPhase("streaming");

    try {
      const stream = adminClient.pairAccount(
        {
          label: trimmedLabel,
          method,
          phoneNumber: method === PairMethod.PHONE_CODE ? trimmedPhone : "",
        },
        // A pairing window outlives the transport's default deadline; <= 0
        // disables it for this call only.
        { signal: controller.signal, timeoutMs: 0 },
      );

      for await (const update of stream) {
        if (controller.signal.aborted) break;

        switch (update.update.case) {
          case "qrCode":
            setQrPayload(update.update.value);
            setRotation((r) => r + 1);
            setSecondsLeft(QR_TTL_SECONDS);
            break;

          case "linkingCode":
            setLinkingCode(update.update.value);
            break;

          case "paired": {
            const account = update.update.value;
            setPairedLabel(account.label || trimmedLabel);
            setPhase("paired");
            toast.success(`${account.label || trimmedLabel} paired`, {
              description:
                "The device session is stored. Review the send policy before sending anything.",
            });
            await invalidateAccounts(queryClient);
            break;
          }

          case "error":
            setError(update.update.value);
            setPhase("error");
            break;

          case "timeout":
            if (update.update.value) setPhase("timeout");
            break;

          case undefined:
            // Unknown oneof member from a newer server; ignore it rather than
            // breaking the stream.
            break;
        }
      }
    } catch (err) {
      // An abort is our own doing (dialog closed, restart pressed) and is not
      // worth reporting.
      if (controller.signal.aborted || isCanceled(err)) return;
      setError(errorMessage(err, "The pairing stream failed."));
      setPhase("error");
    } finally {
      if (abortRef.current === controller) abortRef.current = undefined;
    }
  }, [abortStream, label, method, phone, queryClient]);

  const streaming = phase === "streaming";
  const paired = phase === "paired";
  const locked = streaming || paired;

  function close() {
    abortStream();
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) abortStream();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add WhatsApp account</DialogTitle>
          <DialogDescription>
            Links this service as a companion device. The phone stays the primary device and must
            remain online.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="account-label">Label</Label>
          <Input
            id="account-label"
            placeholder="Campaign primary"
            value={label}
            disabled={locked}
            onChange={(e) => setLabel(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Operator-facing only. Not sent to WhatsApp.
          </p>
        </div>

        <Tabs
          value={method === PairMethod.PHONE_CODE ? "phone" : "qr"}
          onValueChange={(v) => {
            // Switching method restarts pairing from scratch, so the old
            // stream has to go.
            setMethod(v === "phone" ? PairMethod.PHONE_CODE : PairMethod.QR);
            reset();
          }}
        >
          <TabsList className="w-full">
            <TabsTrigger value="qr" className="flex-1" disabled={locked}>
              <QrCodeIcon /> QR code
            </TabsTrigger>
            <TabsTrigger value="phone" className="flex-1" disabled={locked}>
              <SmartphoneIcon /> Phone code
            </TabsTrigger>
          </TabsList>

          <TabsContent value="qr" className="space-y-3 pt-3">
            <ol className="list-inside list-decimal space-y-1 text-xs text-muted-foreground">
              <li>On the phone open WhatsApp → Settings → Linked devices.</li>
              <li>Tap “Link a device”.</li>
              <li>Scan the code below before it rotates.</li>
            </ol>

            {paired ? (
              <PairedPanel label={pairedLabel ?? label} />
            ) : streaming ? (
              qrPayload === undefined ? (
                <WaitingPanel message="Waiting for the first QR code from WhatsApp…" />
              ) : (
                <div className="flex flex-col items-center gap-3">
                  {/* Keyed on the payload so a rotated code remounts cleanly. */}
                  <QrCode key={qrPayload} value={qrPayload} size={232} />
                  <div className="w-full max-w-[232px] space-y-1">
                    <Progress
                      value={(secondsLeft / QR_TTL_SECONDS) * 100}
                      className={cn(
                        "h-1",
                        secondsLeft <= 5 && "[&>[data-slot=progress-indicator]]:bg-tone-warning",
                      )}
                    />
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span className="tabular">
                        {secondsLeft > 0 ? `Rotates in ${secondsLeft}s` : "Rotating…"}
                      </span>
                      <span className="tabular">code #{rotation}</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => void start()}>
                    <RefreshCwIcon /> Restart pairing
                  </Button>
                </div>
              )
            ) : (
              <IdlePanel onStart={() => void start()} icon={<QrCodeIcon />} cta="Generate QR code" />
            )}
          </TabsContent>

          <TabsContent value="phone" className="space-y-3 pt-3">
            <div className="space-y-2">
              <Label htmlFor="pair-phone">Phone number (E.164, no leading +)</Label>
              <Input
                id="pair-phone"
                inputMode="numeric"
                placeholder="381641234567"
                value={phone}
                disabled={locked}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))}
              />
            </div>

            {paired ? (
              <PairedPanel label={pairedLabel ?? label} />
            ) : streaming ? (
              linkingCode === undefined ? (
                <WaitingPanel message="Asking WhatsApp for a linking code…" />
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-col items-center gap-2 rounded-lg border bg-muted/40 p-5">
                    <p className="text-xs text-muted-foreground">
                      Enter this code on the phone under “Link with phone number instead”
                    </p>
                    <p className="tabular font-mono text-3xl font-semibold tracking-[0.2em]">
                      {linkingCode}
                    </p>
                    <div className="flex gap-2">
                      <CopyButton
                        value={linkingCode.replace(/-/g, "")}
                        label="Copy code"
                        size="sm"
                      />
                      <Button variant="outline" size="sm" onClick={() => void start()}>
                        <RefreshCwIcon /> New code
                      </Button>
                    </div>
                  </div>
                </div>
              )
            ) : (
              <IdlePanel
                onStart={() => void start()}
                icon={<SmartphoneIcon />}
                cta="Request phone code"
              />
            )}
          </TabsContent>
        </Tabs>

        {phase === "error" && error ? (
          <Alert variant="destructive">
            <TriangleAlertIcon />
            <AlertTitle>Pairing failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {phase === "timeout" ? (
          <Alert variant="destructive">
            <TriangleAlertIcon />
            <AlertTitle>Pairing window elapsed</AlertTitle>
            <AlertDescription>
              <p>Nothing scanned in time. Start again.</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={() => void start()}>
                <RefreshCwIcon /> Start again
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" size="sm" onClick={close}>
            {paired ? "Close" : "Cancel"}
          </Button>
          {streaming ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2Icon className="size-3.5 animate-spin" /> Waiting for the phone…
            </span>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IdlePanel({
  onStart,
  icon,
  cta,
}: {
  onStart: () => void;
  icon: React.ReactNode;
  cta: string;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8">
      <div className="text-muted-foreground [&>svg]:size-6">{icon}</div>
      <Button size="sm" onClick={onStart}>
        {cta}
      </Button>
    </div>
  );
}

/** Shown between opening the stream and the first update landing. */
function WaitingPanel({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-8 text-center">
      <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
      <p className="text-xs text-muted-foreground">{message}</p>
    </div>
  );
}

function PairedPanel({ label }: { label: string }) {
  return (
    <Alert className="border-tone-success/40 bg-tone-success/5">
      <CheckCircle2Icon className="text-tone-success" />
      <AlertTitle>Paired</AlertTitle>
      <AlertDescription>
        {label || "The account"} is linked. Check the send policy before the first batch — the
        default pacing is deliberately slow.
      </AlertDescription>
    </Alert>
  );
}
